import {
  Sheet as SilkSheet,
  Scroll,
  VisuallyHidden,
  useClientMediaQuery,
} from '@silk-hq/components'
import { XIcon } from 'lucide-react'
import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'

type Side = 'left' | 'right'

const SheetHeaderActionsContext = createContext<
  ((node: ReactNode | null) => void) | null
>(null)

/** Register actions in the sheet chrome (top-right, left of close). */
export function useSheetHeaderActions(
  node: ReactNode | null,
  deps: readonly unknown[]
) {
  const setHeaderActions = useContext(SheetHeaderActionsContext)
  useLayoutEffect(() => {
    if (!setHeaderActions) return
    setHeaderActions(node)
    return () => setHeaderActions(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- caller supplies deps
  }, [setHeaderActions, ...deps])
}

/**
 * Full-viewport long sheet. Pull up or down to dismiss.
 * Desktop: card on the opposite side of the click, ≥ viewport tall.
 * Mobile: content bottom-aligned when short; grows and scrolls when long.
 */
export function Sheet({
  open,
  onClose,
  title,
  wide,
  sheet,
  children,
}: {
  open: boolean
  onClose: () => void
  title?: string
  wide?: boolean
  sheet?: ReactNode
  children: ReactNode
}) {
  const largeViewport = useClientMediaQuery('(min-width: 640px)')
  const [presented, setPresented] = useState(open)
  const [side, setSide] = useState<Side>('right')
  const [headerActions, setHeaderActions] = useState<ReactNode | null>(null)
  const pendingSideRef = useRef<Side>('right')

  useEffect(() => {
    if (open) {
      setSide(pendingSideRef.current)
      setPresented(true)
    } else {
      setPresented(false)
      setHeaderActions(null)
    }
  }, [open])

  const panel = (
    <SheetHeaderActionsContext.Provider value={setHeaderActions}>
      <div
        className={[
          'Sheet-inner bg-white',
          largeViewport ? `Sheet-inner--${side}` : 'Sheet-inner--bottom',
          wide && 'Sheet-inner--wide',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div className="Sheet-chrome">
          {headerActions}
          <SilkSheet.Trigger
            type="button"
            action="dismiss"
            className="Sheet-dismiss"
            aria-label="Sluiten"
          >
            <XIcon className="h-5 w-5" />
          </SilkSheet.Trigger>
        </div>
        {title ? (
          <SilkSheet.Title className="Sheet-title">{title}</SilkSheet.Title>
        ) : (
          <VisuallyHidden.Root>
            <SilkSheet.Title>Dialog</SilkSheet.Title>
          </VisuallyHidden.Root>
        )}
        <div className="Sheet-body">{sheet}</div>
      </div>
    </SheetHeaderActionsContext.Provider>
  )

  return (
    <SilkSheet.Root
      license="non-commercial"
      sheetRole="dialog"
      presented={presented}
      onPresentedChange={setPresented}
    >
      <div
        className="Sheet-page"
        onPointerDownCapture={(e) => {
          if (e.button !== 0) return
          pendingSideRef.current =
            e.clientX < window.innerWidth / 2 ? 'right' : 'left'
        }}
      >
        {children}
      </div>

      <SilkSheet.Portal>
        <SilkSheet.View
          className={
            largeViewport
              ? `Sheet-view Sheet-view--${side}`
              : 'Sheet-view Sheet-view--mobile'
          }
          contentPlacement="center"
          tracks={['top', 'bottom']}
          swipeOvershoot={false}
          nativeEdgeSwipePrevention
          enteringAnimationSettings={{
            easing: 'spring',
            stiffness: 480,
            damping: 45,
            mass: 1.5,
          }}
          onClickOutside={{ dismiss: true, stopOverlayPropagation: true }}
          onTravelStatusChange={(status) => {
            if (status === 'idleOutside') onClose()
          }}
        >
          <SilkSheet.Backdrop
            className="Sheet-backdrop"
            themeColorDimming="auto"
            travelAnimation={{
              opacity: ({ progress }) => Math.min(progress * 0.55, 0.55),
            }}
          />
          <SilkSheet.Content className="Sheet-content" asChild>
            <Scroll.Root className="Sheet-scrollRoot" asChild>
              <Scroll.View
                className="Sheet-scrollView"
                onClick={(e) => {
                  if (!(e.target as Element).closest?.('.Sheet-inner')) {
                    setPresented(false)
                  }
                }}
              >
                <Scroll.Content
                  className={
                    largeViewport
                      ? `Sheet-scrollContent Sheet-scrollContent--desktop Sheet-scrollContent--${side}`
                      : 'Sheet-scrollContent Sheet-scrollContent--mobile'
                  }
                >
                  {panel}
                </Scroll.Content>
              </Scroll.View>
            </Scroll.Root>
          </SilkSheet.Content>
        </SilkSheet.View>
      </SilkSheet.Portal>
    </SilkSheet.Root>
  )
}
